import { useEffect, useMemo, useState } from "react";
import SearchInput from "./shared/SearchInput";
import {
  fetchActivated,
  activatedReportPreview,
  activatedReportSend,
  fetchTkEmailCandidates,
  fetchTkCcDefault,
  saveTkCcDefault,
} from "../utils/api";
import { cssUrl } from "../utils/cssVersion";

const filterTemplateStyles = cssUrl("filterTemplate.css");

/* ============================================================================
 * ActivatedModal — the "Activated" workers register, TestNTagDashboard-style.
 *   · one fetch (fetchActivated) pulls the three main-database views:
 *       Casual Work Positions TFN · Employee Work Position TFN · Contractor Work Position ABN
 *   · every row is cross-checked (backend) against the LOGIN table views —
 *     TFN_Active for casuals + employees, ABN_Active for contractors, BOTH
 *     for New Users (they may already hold a crossover ABN/TFN record) — on
 *     Name = ID Login, Email, Mobile
 *   · row 1 totals: Casuals / Employee / Contractor / New Users (everyone in the view)
 *     — one at a time
 *     row 2 totals: … Pending (not fully activated in the login table)
 *     — any combination; picking from one row clears the other
 *     nothing selected = no table
 *   · ⌛ red  = email + mobile found in login but the NAME differs — the name
 *     cell turns red so admin can fix it on the database
 *     ⌛ amber = name found but email / mobile differ
 *   · search by name, email, mobile or work position
 *   · every row has a checkbox — ticked rows are what the report contains
 *     (all rows tick by default; header checkbox = select / clear all)
 *   · ↻ refresh, ✉ report modal (preview / email PDF) — same recipient pool
 *     and CC-default chips as the Test & Tag report
 * ==========================================================================*/

const toDisplayDate = (v) => {
  const s = (v ?? "").toString().trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s || "—";
};

const initials = (name) =>
  (name ?? "").toString().trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("") || "—";

/* ---------- icons ---------- */
const ClockUserIcon = ({ size = 12 }) => (      // casual
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="7" r="4" /><path d="M2 21v-2a4 4 0 0 1 4-4h4" /><circle cx="17.5" cy="17.5" r="4.5" /><path d="M17.5 15.5v2l1.5 1" />
  </svg>
);
const BadgeUserIcon = ({ size = 12 }) => (      // employee
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2.5" /><path d="M5.5 17a3.5 3.5 0 0 1 7 0" /><path d="M14 9h4M14 13h4" />
  </svg>
);
const UserPlusIcon = ({ size = 12 }) => (       // new user
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8" cy="7" r="4" /><path d="M19 8v6M22 11h-6" />
  </svg>
);
const BriefcaseIcon = ({ size = 12 }) => (      // contractor
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /><path d="M2 13h20" />
  </svg>
);

const HourglassIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2h12" /><path d="M6 22h12" /><path d="M8 2v3.5L12 10l4-4.5V2" /><path d="M8 22v-3.5L12 14l4 4.5V22" />
  </svg>
);

/* row 1 — everyone in the main-database view; key must match the backend `category` tag */
export const ACT_FILTERS = [
  { key: "casual",     label: "Casuals",    color: "#fbbf24", Icon: ClockUserIcon, category: "casual",     pending: false, title: "Casual Work Positions TFN" },
  { key: "employee",   label: "Employee",   color: "#4ade80", Icon: BadgeUserIcon, category: "employee",   pending: false, title: "Employee Work Position TFN" },
  { key: "contractor", label: "Contractor", color: "#22d3ee", Icon: BriefcaseIcon, category: "contractor", pending: false, title: "Contractor Work Position ABN" },
  { key: "new",        label: "New Users",  color: "#a78bfa", Icon: UserPlusIcon,  category: "new",        pending: false, title: "New Staff Work Position — early-stage users" },
];
/* row 2 — same categories, only those NOT fully activated in the login table */
export const ACT_PENDING_FILTERS = [
  { key: "casualPending",     label: "Casuals Pending",    color: "#fbbf24", Icon: HourglassIcon, category: "casual",     pending: true, title: "Casuals not found in TFN_Active (name + email + mobile)" },
  { key: "employeePending",   label: "Employee Pending",   color: "#4ade80", Icon: HourglassIcon, category: "employee",   pending: true, title: "Employees not found in TFN_Active (name + email + mobile)" },
  { key: "contractorPending", label: "Contractor Pending", color: "#22d3ee", Icon: HourglassIcon, category: "contractor", pending: true, title: "Contractors not found in ABN_Active (name + email + mobile)" },
  { key: "newPending",        label: "New Users Pending", color: "#a78bfa", Icon: HourglassIcon, category: "new",        pending: true, title: "New users not found in TFN_Active or ABN_Active — a crossover ABN/TFN record counts as already active" },
];
const ALL_FILTERS = [...ACT_FILTERS, ...ACT_PENDING_FILTERS];
const isPending = (r) => r.activation && r.activation !== "active";

const ACTIVATION_FLAG = {
  name_mismatch:    { color: "#f87171", title: "Email + mobile match the login table but the name differs — fix the name on the database" },
  contact_mismatch: { color: "#fbbf24", title: "Name matches the login table but email / mobile differ" },
};

const S = {
  backdrop: { position:"fixed", inset:0, top: "var(--fr-modal-top, 0px)", background:"rgba(2,6,23,0.7)", zIndex:1100,
    display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"40px 16px", overflowY:"auto" },
  modal: { position:"relative", overflow:"hidden", width:"100%", maxWidth:1040,
    background:"linear-gradient(135deg,#1e293b 0%,#0f172a 100%)",
    border:"1px solid rgba(34,211,238,0.25)", borderRadius:16, padding:"18px 18px 14px" },
  accent: { position:"absolute", top:0, left:0, right:0, height:3, background:"linear-gradient(90deg,#22d3ee,#3b82f6,#8b5cf6)" },
  head: { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 },
  title: { margin:0, fontSize:"1rem", fontWeight:700, color:"#f1f5f9" },
  closeX: { background:"none", border:"none", color:"rgba(255,255,255,0.6)", fontSize:"1.4rem", lineHeight:1, cursor:"pointer" },
  navBtn: { width:32, height:32, borderRadius:9, border:"1px solid rgba(34,211,238,0.4)",
    background:"rgba(34,211,238,0.1)", color:"#22d3ee", cursor:"pointer", fontSize:"0.95rem", lineHeight:1,
    display:"inline-flex", alignItems:"center", justifyContent:"center" },
  eyebrow: { fontSize:"0.62rem", letterSpacing:"0.14em", textTransform:"uppercase", color:"#22d3ee", fontWeight:700 },
  buttonsGrid: { display:"grid", gridTemplateColumns:"repeat(4,minmax(0,1fr))", gap:10, margin:"10px 0 6px" },
  countBtn: (active, color) => ({ borderRadius:12, padding:"11px 8px", cursor:"pointer",
    background: active ? "rgba(34,211,238,0.15)" : "rgba(34,211,238,0.08)",
    border:`1px solid ${active ? color : "rgba(34,211,238,0.25)"}`,
    display:"flex", flexDirection:"column", alignItems:"center", gap:4 }),
  countLabel: { display:"inline-flex", alignItems:"center", gap:5, fontSize:"0.62rem", color:"rgba(255,255,255,0.55)",
    textTransform:"uppercase", letterSpacing:"0.05em", fontWeight:700, textAlign:"center", lineHeight:1.3 },
  countValue: (color) => ({ fontSize:"1.55rem", fontWeight:800, lineHeight:1, color }),
  card: { background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:"12px", marginTop:8 },
  colHead: { fontSize:"0.62rem", letterSpacing:"0.05em", textTransform:"uppercase",
    color:"rgba(255,255,255,0.45)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  cell: { fontSize:"0.82rem", color:"#e2e8f0", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  name: { fontSize:"0.84rem", fontWeight:700, color:"#22d3ee", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  check: { width:16, height:16, accentColor:"#22d3ee", cursor:"pointer" },
  headshot: { width:32, height:32, borderRadius:"50%", objectFit:"cover", display:"block", border:"1px solid rgba(34,211,238,0.3)" },
  headshotEmpty: { width:32, height:32, borderRadius:"50%", border:"1px dashed rgba(34,211,238,0.3)",
    display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.62rem", fontWeight:700, color:"rgba(255,255,255,0.4)" },
  flag: (color) => ({ width:24, height:24, borderRadius:7, display:"inline-flex", alignItems:"center", justifyContent:"center",
    color, background:`${color}1f`, border:`1px solid ${color}66`, flexShrink:0 }),
  empty: { padding:"24px", textAlign:"center", color:"rgba(255,255,255,0.5)", fontStyle:"italic", fontSize:"0.85rem" },
  hint: { marginTop:10, fontSize:"0.72rem", color:"rgba(255,255,255,0.4)", textAlign:"center" },
  closeBtn: { width:"100%", padding:"11px", borderRadius:10, border:"none",
    background:"rgba(255,255,255,0.06)", color:"#e2e8f0", fontWeight:600, cursor:"pointer", marginTop:12 },
  dayChip: (on) => ({ padding:"4px 12px", borderRadius:999, fontSize:"0.72rem", fontWeight:700, cursor:"pointer",
    border:`1px solid ${on ? "rgba(59,130,246,0.7)" : "rgba(255,255,255,0.15)"}`,
    background: on ? "rgba(59,130,246,0.2)" : "rgba(255,255,255,0.03)", color: on ? "#93c5fd" : "rgba(255,255,255,0.55)" }),
};

/* ☐ | Headshot | Name | Email | Mobile | Start Work | Work Position | flags */
const TABLE_COLS = "28px 44px 1.3fr 1.6fr 1fr 0.9fr 1.2fr 64px";

/* ---------- nested overlay styles (recipients + send) — same as TestNTagDashboard ---------- */
const O = {
  overlay: { position:"fixed", inset:0, top: "var(--fr-modal-top, 0px)", background:"rgba(0,0,0,0.75)", backdropFilter:"blur(6px)",
    display:"flex", alignItems:"center", justifyContent:"center", zIndex:1200, padding:20 },
  modal: { background:"linear-gradient(135deg,#0f172a 0%,#1e293b 100%)", border:"1px solid rgba(34,211,238,0.25)",
    borderRadius:16, width:"100%", maxWidth:640, overflow:"hidden", boxShadow:"0 25px 80px rgba(0,0,0,0.6)" },
  bar: { height:3, background:"linear-gradient(90deg,#22d3ee,#3b82f6,#8b5cf6)" },
  header: { padding:"16px 22px 8px", display:"flex", justifyContent:"space-between", alignItems:"center" },
  hTitle: { fontSize:"1rem", fontWeight:700, color:"#f1f5f9", margin:0 },
  body: { padding:"8px 22px 4px", maxHeight:"min(60vh, calc(100dvh - var(--fr-modal-top, 0px) - 200px))", overflowY:"auto" },
  footer: { padding:"14px 22px", display:"flex", justifyContent:"flex-end", gap:10,
    background:"rgba(0,0,0,0.2)", borderTop:"1px solid rgba(255,255,255,0.06)" },
  cancel: { background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.7)", border:"1px solid rgba(255,255,255,0.1)",
    borderRadius:10, padding:"10px 18px", fontSize:"0.85rem", fontWeight:600, cursor:"pointer" },
  confirm: (busy) => ({ background:"linear-gradient(135deg,#22d3ee 0%,#3b82f6 100%)", color:"#0f172a", border:"none",
    borderRadius:10, padding:"10px 20px", fontSize:"0.85rem", fontWeight:700,
    cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1, display:"inline-flex", alignItems:"center", gap:8 }),
  row: { display:"grid", gridTemplateColumns:"28px 1.2fr 1fr 1.4fr", alignItems:"center",
    columnGap:8, padding:"9px 4px", borderBottom:"1px solid rgba(255,255,255,0.06)" },
  rName: { fontSize:"0.85rem", fontWeight:700, color:"#22d3ee", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  rCell: { fontSize:"0.8rem", color:"#e2e8f0", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  pill: { fontSize:"0.72rem", color:"rgba(255,255,255,0.55)" },
  check: { width:16, height:16, accentColor:"#22d3ee", cursor:"pointer" },
  note: { padding:"8px 12px", borderRadius:8, margin:"8px 0", fontSize:"0.78rem",
    background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.35)", color:"#4ade80" },
  err: { padding:"8px 12px", borderRadius:8, margin:"8px 0", fontSize:"0.78rem",
    background:"rgba(248,113,113,0.08)", border:"1px solid rgba(248,113,113,0.35)", color:"#fca5a5" },
};

const Spin = () => (
  <span style={{ width:12, height:12, border:"2px solid currentColor", borderTopColor:"transparent",
    borderRadius:"50%", display:"inline-block", animation:"act-spin 0.7s linear infinite" }} />
);

/* ---------- send report — recipient pool + CC-default chips (same as TestNTag) ---------- */
const SendModal = ({ filterHeader, reportRows, summary, onClose }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState("");             // "preview" | "send"
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  const CC_CATEGORIES = [
    { key: "supervisor", label: "Supervisors" },
    { key: "admin",      label: "Admin" },
    { key: "operations", label: "Operations" },
  ];
  const catOf = (r) => r.category || "operations";
  const [chips, setChips] = useState([]);

  useEffect(() => {
    Promise.all([fetchTkEmailCandidates(), fetchTkCcDefault()]).then(([cand, def]) => {
      const candidates = cand.status === "success" ? cand.candidates || [] : [];
      const active = def.status === "success" && Array.isArray(def.chips) ? def.chips : ["supervisor"];
      setList(candidates);
      setChips(active);
      const pre = {};
      candidates.forEach((r) => { if (active.includes(catOf(r))) pre[r.email] = true; });
      setChecked(pre);
      setLoading(false);
    });
  }, []);

  const toggleChip = (key) => {
    const next = chips.includes(key) ? chips.filter((c) => c !== key) : [...chips, key];
    setChips(next);
    const pre = {};
    list.forEach((r) => { if (next.includes(catOf(r))) pre[r.email] = true; });
    setChecked(pre);
    saveTkCcDefault(next);
  };
  const allChecked = list.length > 0 && list.every((r) => checked[r.email]);
  const toggleAll = () => {
    const next = {};
    if (!allChecked) list.forEach((r) => { next[r.email] = true; });
    setChecked(next);
  };
  const recipients = list.filter((r) => checked[r.email]).map((r) => ({ name: r.name, email: r.email }));

  const preview = async () => {
    setBusy("preview"); setError(""); setNote("");
    const res = await activatedReportPreview(filterHeader, reportRows, summary);
    if (res.status === "success") window.open(URL.createObjectURL(res.blob), "_blank");
    else setError(res.error || "Preview failed");
    setBusy("");
  };
  const send = async () => {
    setBusy("send"); setError(""); setNote("");
    const res = await activatedReportSend(filterHeader, reportRows, recipients, summary);
    if (res.status === "success") {
      setNote(`Sent — ${recipients.length} recipient${recipients.length === 1 ? "" : "s"} CC'd.`);
    } else setError(res.error || "Send failed");
    setBusy("");
  };

  return (
    <div style={O.overlay} onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
      <div style={O.modal}>
        <div style={O.bar} />
        <div style={O.header}>
          <h5 style={O.hTitle}>✉ Send Activated Report</h5>
          <span style={O.pill}>{reportRows.length} selected</span>
        </div>
        <div style={O.body}>
          <div style={{ ...O.pill, margin:"2px 0 8px" }}>{filterHeader}</div>
          <div style={{ display:"flex", gap:6, margin:"4px 0 10px" }}>
            {CC_CATEGORIES.map((c) => (
              <button key={c.key} type="button" style={S.dayChip(chips.includes(c.key))} onClick={() => toggleChip(c.key)}>
                {c.label}
              </button>
            ))}
          </div>
          {note && <div style={O.note}>{note}</div>}
          {error && <div style={O.err}>{error}</div>}
          <div style={{ ...O.row, borderBottom:"1px solid rgba(255,255,255,0.12)" }}>
            <input type="checkbox" style={O.check} checked={allChecked} onChange={toggleAll} title="Select all" />
            {["Name", "Job", "Email"].map((h) => (
              <span key={h} style={{ fontSize:"0.62rem", textTransform:"uppercase", letterSpacing:"0.05em",
                color:"rgba(255,255,255,0.45)", fontWeight:700 }}>{h}</span>
            ))}
          </div>
          {loading ? (
            <div style={{ padding:20, textAlign:"center", color:"rgba(255,255,255,0.5)" }}>Loading…</div>
          ) : list.map((r) => (
            <div key={r.email} style={O.row}>
              <input type="checkbox" style={O.check} checked={!!checked[r.email]}
                onChange={() => setChecked((p) => ({ ...p, [r.email]: !p[r.email] }))} />
              <span style={O.rName}>{r.name}</span>
              <span style={O.rCell}>{r.job || "—"}</span>
              <span style={O.rCell}>{r.email}</span>
            </div>
          ))}
        </div>
        <div style={O.footer}>
          <button type="button" style={O.cancel} onClick={onClose}>Close</button>
          <button type="button" style={O.confirm(busy === "preview")} onClick={preview} disabled={!!busy || reportRows.length === 0}>
            {busy === "preview" ? <Spin /> : "👁"} Preview Report
          </button>
          <button type="button" style={O.confirm(busy === "send")} onClick={send} disabled={!!busy || reportRows.length === 0}>
            {busy === "send" ? <Spin /> : "✉"} Send Emails ({recipients.length})
          </button>
        </div>
      </div>
    </div>
  );
};

/* ============================================================================ */
const ActivatedModal = ({ onClose }) => {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState(null);          // row 1: one at a time
  const [pendingFilters, setPendingFilters] = useState([]); // row 2: any combination
  const [checked, setChecked] = useState({});          // rowKey -> bool
  const [sendOpen, setSendOpen] = useState(false);

  const rowKey = (r) => `${r.category}:${r.recordId}`;

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = filterTemplateStyles;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !sendOpen) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, sendOpen]);

  const load = async () => {
    const res = await fetchActivated();
    if (res.status !== "success") throw new Error(res.error || "Failed to load activated workers");
    return res.data ?? [];
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try { const rows = await load(); if (alive) { setAll(rows); setLoadError(null); } }
      catch (err) { if (alive) setLoadError(err.message); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  /* soft refresh — refetch in place, keep filters / ticks / search */
  const refresh = async () => {
    if (refreshing || loading) return;
    setRefreshing(true);
    try { setAll(await load()); setLoadError(null); } catch { /* keep current rows */ }
    setRefreshing(false);
  };

  /* search scope (totals compute on this) */
  const scoped = useMemo(() => {
    const q = query.toLowerCase().replace(/\s+/g, "");
    if (!q) return all;
    return all.filter((r) =>
      `${r.name}${r.email}${r.mobile}${r.workPosition}`.toLowerCase().replace(/\s+/g, "").includes(q)
    );
  }, [all, query]);

  const rowsFor = (f, src) => src.filter((r) => r.category === f.category && (!f.pending || isPending(r)));

  const counts = useMemo(() => {
    const c = {};
    ALL_FILTERS.forEach((f) => { c[f.key] = rowsFor(f, scoped).length; });
    return c;
  }, [scoped]);

  /* the buttons currently lit: one from row 1, or any number from row 2 */
  const activeDefs = useMemo(() => {
    if (filter) return ACT_FILTERS.filter((f) => f.key === filter);
    return ACT_PENDING_FILTERS.filter((f) => pendingFilters.includes(f.key));
  }, [filter, pendingFilters]);
  const isPendingView = activeDefs.some((f) => f.pending);
  const activeLabel = activeDefs.map((f) => f.label).join(" + ");
  const activeColor = activeDefs.length === 1 ? activeDefs[0].color : "#22d3ee";

  /* table rows: union of the lit buttons, sorted by name */
  const visible = useMemo(() => {
    if (!activeDefs.length) return [];
    const seen = new Set();
    const out = [];
    activeDefs.forEach((f) => rowsFor(f, scoped).forEach((r) => {
      const k = rowKey(r);
      if (!seen.has(k)) { seen.add(k); out.push(r); }
    }));
    return out.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  }, [scoped, activeDefs]);

  /* newly visible rows tick by default */
  useEffect(() => {
    setChecked((prev) => {
      const next = { ...prev };
      visible.forEach((r) => { const k = rowKey(r); if (!(k in next)) next[k] = true; });
      return next;
    });
  }, [visible]);

  const toggleFilter = (key) => {           // row 1 — single select, clears row 2
    setPendingFilters([]);
    setFilter((p) => (p === key ? null : key));
  };
  const togglePending = (key) => {          // row 2 — multi select, clears row 1
    setFilter(null);
    setPendingFilters((p) => (p.includes(key) ? p.filter((x) => x !== key) : [...p, key]));
  };
  const selectedRows = visible.filter((r) => checked[rowKey(r)]);
  const allChecked = visible.length > 0 && selectedRows.length === visible.length;
  const toggleAll = () =>
    setChecked((p) => {
      const next = { ...p };
      visible.forEach((r) => { next[rowKey(r)] = !allChecked; });
      return next;
    });

  const filterHeader = useMemo(() => {
    const parts = ["Activated workers"];
    parts.push(activeDefs.length ? `List: ${activeLabel}` : "List: none");
    if (query.trim()) parts.push(`Search: "${query.trim()}"`);
    parts.push(`${selectedRows.length} of ${visible.length} selected`);
    return parts.join(" · ");
  }, [activeDefs, activeLabel, query, selectedRows.length, visible.length]);

  const summary = useMemo(() => ({
    totalItems: visible.length,
    selected: selectedRows.length,
    counts,
  }), [visible.length, selectedRows.length, counts]);

  const noteFor = (r) => {
    if (r.activation === "name_mismatch") return `Name differs — login has "${r.loginName}"`;
    if (r.activation === "contact_mismatch") return "Email / mobile differ from login";
    if (r.activation === "pending") return "Not in login table";
    return "";
  };

  /* ticked rows serialised for the PDF backend */
  const reportRows = useMemo(() => selectedRows.map((r) => ({
    "Name": r.name, "Email": r.email, "Mobile": r.mobile,
    "Start Work": r.startWork, "Work Position": r.workPosition,
    "Category": (isPendingView ? ACT_PENDING_FILTERS : ACT_FILTERS).find((f) => f.category === r.category)?.label ?? r.category,
    "Activation": r.activation || "",
    "Note": noteFor(r),
  })), [selectedRows, isPendingView]);

  return (
    <div style={S.backdrop} onClick={onClose}>
      <style>{`@keyframes act-spin{to{transform:rotate(360deg)}}
        .act-row{transition:background .12s ease}.act-row:hover{background:rgba(34,211,238,0.06)}`}</style>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.accent} />
        <div style={S.head}>
          <h4 style={S.title}>Activated</h4>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <button type="button" style={{ ...S.navBtn, opacity: refreshing ? 0.6 : 1 }}
              title="Refresh data — keeps current filters" onClick={refresh}>
              {refreshing ? <Spin /> : "↻"}
            </button>
            <button type="button" style={{ ...S.navBtn, opacity: selectedRows.length ? 1 : 0.5 }}
              title={selectedRows.length ? "Send report for the ticked rows" : "Tick some rows first"}
              onClick={() => selectedRows.length && setSendOpen(true)}>✉</button>
            <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>

        <SearchInput value={query} onChange={setQuery} placeholder="Search name, email, mobile or work position…" />

        {/* row 1 — everyone in the view · row 2 — not yet activated in the login table
            (one button at a time across both rows) */}
        {[ACT_FILTERS, ACT_PENDING_FILTERS].map((group, gi) => (
          <div key={gi} style={{ ...S.buttonsGrid, margin: gi ? "0 0 6px" : "10px 0 10px" }}>
            {group.map((f) => {
              const active = gi === 0 ? filter === f.key : pendingFilters.includes(f.key);
              return (
                <button key={f.key} type="button" style={S.countBtn(active, f.color)}
                  title={`${f.title}${gi ? " — combine with the other pending lists" : ""}`}
                  onClick={() => (gi === 0 ? toggleFilter(f.key) : togglePending(f.key))}>
                  <span style={S.countLabel}><span style={{ color:f.color, display:"inline-flex" }}><f.Icon size={12} /></span>{f.label}</span>
                  <span style={S.countValue(f.color)}>{loading ? "…" : counts[f.key]}</span>
                </button>
              );
            })}
          </div>
        ))}

        {/* checkbox table */}
        <div style={S.card}>
          {loading ? (
            <div style={S.empty}>Loading activated workers…</div>
          ) : loadError ? (
            <div style={{ ...S.empty, color:"#fca5a5" }}>{loadError}</div>
          ) : !activeDefs.length ? (
            <div style={S.empty}>Click a button above to list the records — the Pending buttons can be combined.</div>
          ) : visible.length === 0 ? (
            <div style={S.empty}>No records for this view.</div>
          ) : (
            <>
              <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", marginBottom:8 }}>
                <span style={{ ...S.eyebrow, color: activeColor }}>{activeLabel}</span>
                <span style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.45)" }}>
                  {selectedRows.length} of {visible.length} selected
                </span>
              </div>
              <div style={{ maxHeight:420, overflowY:"auto", overflowX:"auto" }}>
                <div style={{ minWidth:820 }}>
                  <div style={{ display:"grid", gridTemplateColumns:TABLE_COLS, alignItems:"center", padding:"4px 8px 8px",
                    columnGap:8, borderBottom:"1px solid rgba(255,255,255,0.12)", position:"sticky", top:0, zIndex:1, background:"#1b2537" }}>
                    <input type="checkbox" style={S.check} checked={allChecked} onChange={toggleAll} title="Select all" />
                    {["", "Name", "Email", "Mobile", "Start Work", "Work Position", ""].map((h, i) => (
                      <span key={i} style={S.colHead}>{h}</span>
                    ))}
                  </div>
                  {visible.map((r, i) => {
                    const k = rowKey(r);
                    const f = ACT_FILTERS.find((x) => x.key === r.category);
                    const flag = ACTIVATION_FLAG[r.activation];
                    const nameColor = r.activation === "name_mismatch" ? "#f87171" : "#22d3ee";
                    return (
                      <div key={k} className="act-row"
                        onClick={() => setChecked((p) => ({ ...p, [k]: !p[k] }))}
                        style={{ display:"grid", gridTemplateColumns:TABLE_COLS, alignItems:"center", cursor:"pointer",
                          padding:"7px 8px", columnGap:8, borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none",
                          opacity: checked[k] ? 1 : 0.55 }}>
                        <input type="checkbox" style={S.check} checked={!!checked[k]}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => setChecked((p) => ({ ...p, [k]: !p[k] }))} />
                        <span>
                          {r.photo
                            ? <img src={r.photo} alt={r.name || "Headshot"} style={S.headshot} />
                            : <span style={S.headshotEmpty}>{initials(r.name)}</span>}
                        </span>
                        <span style={{ ...S.name, color: nameColor }}
                          title={r.activation === "name_mismatch" ? `Login has: ${r.loginName}` : r.name}>
                          {r.name || "—"}
                        </span>
                        <span style={S.cell} title={r.email}>{r.email || "—"}</span>
                        <span style={S.cell}>{r.mobile || "—"}</span>
                        <span style={S.cell}>{toDisplayDate(r.startWork)}</span>
                        <span style={S.cell} title={r.workPosition}>{r.workPosition || "—"}</span>
                        <span style={{ display:"flex", justifyContent:"flex-end", gap:5 }}>
                          {flag && <span style={S.flag(flag.color)} title={flag.title}><HourglassIcon size={13} /></span>}
                          {f && <span style={S.flag(f.color)} title={f.label}><f.Icon /></span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={S.hint}>
                Ticked rows are what the ✉ report contains. Fields are read-only.
                {isPendingView && (
                  <>
                    {" "}<span style={{ color:"#f87171" }}>⌛ red</span> = email + mobile found in login but the name differs (fix the name on the database).
                    {" "}<span style={{ color:"#fbbf24" }}>⌛ amber</span> = name found but email / mobile differ.
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <button type="button" style={S.closeBtn} onClick={onClose}>Close</button>
        {sendOpen && (
          <SendModal filterHeader={filterHeader} reportRows={reportRows} summary={summary} onClose={() => setSendOpen(false)} />
        )}
      </div>
    </div>
  );
};

export default ActivatedModal;